import {
  Button,
  Dialog,
  DialogButtons,
  EmailsChipInput,
  IconTip,
  Spinner,
  TextArea
} from '@st/theme'
import { formatFolderEntityName } from './st-folder-display'
import { match, P } from '@st/pattern'
import {
  saveAndCloseClientOrganizer,
  type STSendClientOrganizerMessage,
  type STSendClientOrganizerView
} from './st-send-client-organizer-module'

/**
 * There's a screen which is table of folders in which an accountant
 * can send out questionnaires to all of them in bulk.
 *
 * In some cases they want to edit the message for a single client or
 * edit the emails which determine who gets sent the questionnaire.
 *
 * This dialog provides that editing functionality.
 */
export function STBulkSendEditQuestionnaireDialog({
  state,
  send,
  onClose
}: {
  state: STSendClientOrganizerView
  send: (message: STSendClientOrganizerMessage) => void
  onClose: () => void
}) {
  const closeDialog = onClose

  if (state.status == 'loading') {
    return (
      <Dialog
        title="Email"
        className="w-full max-w-2xl"
        buttons={
          <DialogButtons>
            <Button variant="subtle" onClick={closeDialog}>
              Close
            </Button>
          </DialogButtons>
        }
      >
        <div className="flex items-center justify-center py-16">
          <Spinner className="size-8 fill-blue-500 text-blue-100" />
        </div>
      </Dialog>
    )
  }

  if (state.status == 'loadFailed') {
    return (
      <Dialog
        title="Email"
        className="w-full max-w-2xl"
        buttons={
          <DialogButtons>
            <Button variant="subtle" onClick={closeDialog}>
              Close
            </Button>
          </DialogButtons>
        }
      >
        <div className="flex flex-col gap-2 py-8 text-sm text-gray-700">
          <div className="font-semibold text-gray-900">Failed to load email draft.</div>
          <div>Please close this dialog and try again.</div>
        </div>
      </Dialog>
    )
  }

  return (
    <STBulkSendEditQuestionnaireDialogView
      state={state}
      send={send}
      onClose={closeDialog}
      onSave={() => send(saveAndCloseClientOrganizer())}
    />
  )
}

function STBulkSendEditQuestionnaireDialogView({
  state,
  onSave,
  onClose,
  send
}: {
  state: Exclude<
    STSendClientOrganizerView,
    { status: 'idle' | 'finishing' | 'loading' | 'loadFailed' }
  >
  send: (message: STSendClientOrganizerMessage) => void
  onSave: () => void
  onClose: () => void
}) {
  return (
    <Dialog
      title={`Email to ${formatFolderEntityName(state.sendState.folder?.entities)}`}
      subtitle={`You can edit the email for ${formatFolderEntityName(
        state.sendState.folder?.entities
      )} below.`}
      className="w-full max-w-2xl"
      buttons={
        <DialogButtons>
          <Button variant="subtle" disabled={state.status == 'savingAndClosing'} onClick={onClose}>
            Cancel
          </Button>

          <Button variant="primary" disabled={state.status != 'loaded'} onClick={onSave}>
            {match(state.status)
              .with(P.union('saving', 'savingAndClosing'), () => 'Saving...')
              .otherwise(() => 'Save')}
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col gap-2">
        <div className="">
          <label className="font-semibold">To:</label>
          <EmailsChipInput
            className="w-full"
            readOnly={
              state.status == 'savingAndClosing' ||
              state.status == 'sending' ||
              state.status == 'sent'
            }
            emails={state.editedRecipients.map((r) => r.email)}
            inputValue={state.inputValue}
            onInputChange={(email) => send({ type: 'emailTyped', email })}
            selectedEmail={state.selectedEmail}
            onSelectedEmailChange={(email) => send({ type: 'emailSelected', email })}
            onAdd={(email) => send({ type: 'emailAdded', email })}
            onDelete={(email) => send({ type: 'emailRemoved', email })}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <label className="font-semibold">Subject:</label>
            <IconTip
              icon="info"
              body="You can change the subject in Settings → Templates"
              className="text-gray-500"
            />
          </div>
          <div className="rounded-lg px-3 py-2 text-gray-900 ring-1 ring-inset ring-gray-200">
            {state.sendState.subject}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-semibold">Message:</label>
          <TextArea
            className="min-h-96"
            placeholder="Send a message"
            readOnly={
              state.status == 'savingAndClosing' ||
              state.status == 'sending' ||
              state.status == 'sent'
            }
            value={state.body}
            onChange={(body) => send({ type: 'bodyChanged', body })}
          />
        </div>
      </div>
    </Dialog>
  )
}
